import { ADDRESS_COUNTRIES, countryName, isValidPostalCode, type AddressParts, type AddressProblem } from "@szerviz/core";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Help } from "./Help";

type Part = keyof AddressParts;

/**
 * An address in the parts invoicing needs (owner, 2026-11-04; Számlázz.hu and NAV want the postal code, the city
 * and the street separately): country (default Hungary), postal code, city and one line for the street, house
 * number, floor and door. Used for the seat and the workshop address in Settings and in the contract dialog, and
 * for the customers. A wrong Hungarian postal code (not 4 digits) is marked red at once.
 *
 * With a `legend` the parts sit in their own box and every input is named "<legend>: <part>" for screen readers
 * (two addresses on one form stay apart); without one the visible captions are the names.
 */
export function AddressFields({
  value, onChange, legend, help, idPrefix, disabled, labels, required = {}, invalid = {}, note,
}: {
  value: AddressParts;
  onChange: (next: AddressParts) => void;
  legend?: string;
  help?: string;
  idPrefix: string;
  disabled?: boolean;
  /** Own captions (the customer form keeps its own words); the address.* texts otherwise. */
  labels?: Partial<Record<Part, string>>;
  required?: Partial<Record<Part, boolean>>;
  /** Parts to mark red (e.g. missing required data). */
  invalid?: Partial<Record<Part, boolean>>;
  /** A note under the fields (e.g. the old address could not be split). */
  note?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const caption = (p: Part) => labels?.[p] ?? t(`address.${p}`);
  const name = (p: Part) => (legend ? `${legend}: ${caption(p)}` : caption(p));
  const countries = useMemo(() => {
    const named = ADDRESS_COUNTRIES.map((c) => ({ code: c, name: countryName(c, i18n.language) }));
    // Hungary first, then by name in the user's language; a stored country outside the list is kept
    const rest = named.filter((c) => c.code !== "HU").sort((a, b) => a.name.localeCompare(b.name, i18n.language));
    const extra = ADDRESS_COUNTRIES.includes(value.country) ? [] : [{ code: value.country, name: countryName(value.country, i18n.language) }];
    return [...named.filter((c) => c.code === "HU"), ...extra, ...rest];
  }, [i18n.language, value.country]);
  const postalBad = !!value.postalCode.trim() && !isValidPostalCode(value.postalCode, value.country);
  const set = (p: Part) => (v: string) => onChange({ ...value, [p]: v });
  const cls = (p: Part, bad = false) => ["field", p === "street" && "af-street", (invalid[p] || bad) && "invalid"].filter(Boolean).join(" ");
  const star = (p: Part) => required[p] && <span className="req" aria-hidden>{" "}*</span>;

  const fields = (
    <div className="address-fields">
      <label className={cls("country")}>
        <span className="field-label">{caption("country")}{star("country")}</span>
        <select id={`${idPrefix}-country`} value={value.country} disabled={disabled} aria-label={name("country")} onChange={(e) => set("country")(e.target.value)}>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className={cls("postalCode", postalBad)}>
        <span className="field-label">{caption("postalCode")}{star("postalCode")}</span>
        <input
          id={`${idPrefix}-postal`}
          className="mono"
          value={value.postalCode}
          disabled={disabled}
          inputMode={value.country === "HU" ? "numeric" : "text"}
          maxLength={10}
          autoComplete="postal-code"
          aria-label={name("postalCode")}
          aria-invalid={invalid.postalCode || postalBad || undefined}
          onChange={(e) => set("postalCode")(e.target.value)}
        />
      </label>
      <label className={cls("city")}>
        <span className="field-label">{caption("city")}{star("city")}</span>
        <input id={`${idPrefix}-city`} value={value.city} disabled={disabled} autoComplete="address-level2" aria-label={name("city")} aria-invalid={invalid.city || undefined} onChange={(e) => set("city")(e.target.value)} />
      </label>
      {/* the reason under the row, the whole width (not squeezed under the narrow postal code field) */}
      {postalBad && <p className="af-error" role="alert">{t(value.country === "HU" ? "address.postalCodeHu" : "address.postalCodeBad")}</p>}
      <label className={cls("street")}>
        <span className="field-label">{caption("street")}{star("street")}</span>
        <input
          id={`${idPrefix}-street`}
          value={value.street}
          disabled={disabled}
          autoComplete="street-address"
          placeholder={t("address.streetPlaceholder")}
          aria-label={name("street")}
          aria-invalid={invalid.street || undefined}
          onChange={(e) => set("street")(e.target.value)}
        />
      </label>
      {note && <p className="af-note warn-box">{note}</p>}
    </div>
  );
  if (!legend) return fields;
  return (
    <fieldset className="address-box wide">
      <legend>
        {legend}
        {help && <Help id={help} />}
      </legend>
      {fields}
    </fieldset>
  );
}

/** The names of what is wrong with an address, for the "still missing" list next to the grey button. */
export function addressProblemNames(problems: AddressProblem[], legend: string, t: (k: string) => string): string[] {
  return problems.map((p) => `${legend}: ${p === "postalCodeFormat" ? t("address.postalCodeBadShort") : t(`address.${p}`)}`);
}
